// Canvas elements as the server and the app keep them (board-core/BoardElement.kt): Excalidraw JSON,
// camelCase, unknown fields kept when an element is written back. Merging per element like
// BoardReconcile.kt: higher version wins, then lower versionNonce, then the lower JSON text.
// Ported to TypeScript instead of Kotlin Multiplatform (docs/web-konzept.md, decision 3, changed):
// the server speaks plain JSON for canvases and the drawing code is not in board-core anyway.

import { generateKeyBetween } from "fractional-indexing";

export type ElementType = "rectangle" | "ellipse" | "diamond" | "text" | "arrow" | "line" | "freedraw" | "image";

export const CUSTOM_SHAPES = ["triangle", "parallelogram", "trapezoid", "pentagon", "hexagon", "star", "block_arrow", "cylinder", "cloud", "document"] as const;
export type CustomShape = (typeof CUSTOM_SHAPES)[number];

export interface Binding {
  elementId: string;
  focus: number;
  gap: number;
}

/** One element. Fields the client does not know stay in the object and go back unchanged. */
export interface El {
  id: string;
  type: ElementType;
  x: number;
  y: number;
  width: number;
  height: number;
  angle?: number;
  strokeColor?: string;
  backgroundColor?: string;
  fillStyle?: string;
  strokeWidth?: number;
  strokeStyle?: string;
  roughness?: number;
  opacity?: number;
  groupIds?: string[];
  index?: string | null;
  seed?: number;
  version?: number;
  versionNonce?: number;
  isDeleted?: boolean;
  boundElements?: { id: string; type: string }[] | null;
  locked?: boolean;
  updated?: number;
  roundness?: { type: number } | null;
  text?: string;
  originalText?: string;
  fontSize?: number;
  fontFamily?: number;
  textAlign?: string;
  verticalAlign?: string;
  containerId?: string | null;
  lineHeight?: number;
  points?: [number, number][];
  pressures?: number[];
  simulatePressure?: boolean;
  startBinding?: Binding | null;
  endBinding?: Binding | null;
  startArrowhead?: string | null;
  endArrowhead?: string | null;
  elbowed?: boolean;
  fileId?: string | null;
  scale?: [number, number];
  customData?: { shape?: string; [k: string]: unknown } | null;
  [unknown: string]: unknown;
}

export const FONT_EXCALIFONT = 5;
export const FONT_NORMAL = 2;
export const NOTE_YELLOW = "#ffec99";

export const STROKES = ["#1e1e1e", "#e03131", "#2f9e44", "#1971c2", "#f08c00", "#9c36b5", "#c2255c", "#6741d9", "#0c8599", "#099268", "#e8590c", "#846358", "#868e96", "#ffffff"];
export const FILLS = ["transparent", "#ffc9c9", "#b2f2bb", "#a5d8ff", "#ffec99", "#eebefa", "#fcc2d7", "#d0bfff", "#99e9f2", "#96f2d7", "#ffd8a8", "#eaddd7", "#e9ecef", "#ffffff"];

export type Style = "sketch" | "clean";

/** Does [incoming] replace [current]? Identical states do not (BoardReconcile.wins). */
export function wins(incoming: El, current: El | undefined): boolean {
  if (!current) return true;
  const iv = incoming.version ?? 1;
  const cv = current.version ?? 1;
  if (iv !== cv) return iv > cv;
  const inonce = incoming.versionNonce ?? 0;
  const cnonce = current.versionNonce ?? 0;
  if (inonce !== cnonce) return inonce < cnonce;
  const a = JSON.stringify(incoming);
  const b = JSON.stringify(current);
  return a !== b && a < b;
}

/** Merges [incoming] into [current] in place; returns the states that lost. */
export function merge(current: Map<string, El>, incoming: El[]): El[] {
  const rejected: El[] = [];
  for (const e of incoming) {
    if (wins(e, current.get(e.id))) current.set(e.id, e);
    else rejected.push(e);
  }
  return rejected;
}

/** A local edit of [e] (BoardMutator.bump): one version up, a new nonce, now as "updated". */
export function bump(e: El, patch: Partial<El> = {}, now = Date.now()): El {
  return { ...e, ...patch, version: (e.version ?? 1) + 1, versionNonce: Math.floor(Math.random() * 2 ** 31), updated: now };
}

export function newId(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-";
  const bytes = crypto.getRandomValues(new Uint8Array(21));
  return Array.from(bytes, (b) => chars[b & 63]).join("");
}

/** Elements to draw, bottom first (fractional index, then id). */
export function sorted(elements: Iterable<El>): El[] {
  return [...elements].filter((e) => !e.isDeleted).sort((a, b) => cmp(a.index ?? "", b.index ?? "") || cmp(a.id, b.id));
}

const cmp = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/** An index above everything on the canvas. */
export function indexAbove(elements: Iterable<El>): string {
  let top: string | null = null;
  for (const e of elements) if (e.index && (top === null || e.index > top)) top = e.index;
  try {
    return generateKeyBetween(top, null);
  } catch {
    return generateKeyBetween(null, null);
  }
}

/** A new element at version 1 in [style] (ElementFactory + BoardStyles). */
export function create(type: ElementType, at: { x: number; y: number; width?: number; height?: number }, style: Style, all: Iterable<El>, extra: Partial<El> = {}): El {
  const sketch = style === "sketch";
  const shape = type === "rectangle" || type === "ellipse" || type === "diamond";
  return {
    id: newId(),
    type,
    x: at.x,
    y: at.y,
    width: at.width ?? 0,
    height: at.height ?? 0,
    angle: 0,
    strokeColor: "#1e1e1e",
    backgroundColor: "transparent",
    fillStyle: sketch ? "hachure" : "solid",
    strokeWidth: 2,
    strokeStyle: "solid",
    roughness: sketch ? 1 : 0,
    opacity: 100,
    groupIds: [],
    frameId: null,
    index: indexAbove(all),
    roundness: shape && type !== "ellipse" && !sketch ? { type: 3 } : null,
    seed: 1 + Math.floor(Math.random() * 2 ** 30),
    version: 1,
    versionNonce: Math.floor(Math.random() * 2 ** 31),
    isDeleted: false,
    boundElements: null,
    updated: Date.now(),
    link: null,
    locked: false,
    ...(type === "text" ? { text: "", originalText: "", fontSize: 20, fontFamily: sketch ? FONT_EXCALIFONT : FONT_NORMAL, textAlign: "left", verticalAlign: "top", containerId: null, lineHeight: 1.25, autoResize: true } : {}),
    ...(type === "arrow" || type === "line" ? { points: [[0, 0], [at.width ?? 0, at.height ?? 0]] as [number, number][], startBinding: null, endBinding: null, startArrowhead: null, endArrowhead: type === "arrow" ? "arrow" : null, elbowed: false } : {}),
    ...(type === "freedraw" ? { points: [[0, 0]] as [number, number][], pressures: [], simulatePressure: true } : {}),
    ...(type === "image" ? { fileId: null, scale: [1, 1] as [number, number], status: "saved" } : {}),
    ...extra,
  };
}

/** The style an element is drawn in (BoardStyles.of). */
export function styleOf(e: El): Style {
  if (e.type === "text") return e.fontFamily === FONT_EXCALIFONT ? "sketch" : "clean";
  return (e.roughness ?? 1) === 0 ? "clean" : "sketch";
}

export function isStickyNote(e: El): boolean {
  return e.type === "rectangle" && !e.customData?.shape && e.fillStyle === "solid" && e.roughness === 0 && (e.backgroundColor ?? "").toLowerCase() === NOTE_YELLOW;
}

/** The custom shape of a rectangle, if it is one (customData.shape). */
export function customShape(e: El): CustomShape | null {
  const s = e.customData?.shape;
  return e.type === "rectangle" && typeof s === "string" && (CUSTOM_SHAPES as readonly string[]).includes(s) ? (s as CustomShape) : null;
}

export interface Box {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** The unrotated bounds; lines and freedraw by their points. */
export function bounds(e: El): Box {
  if ((e.type === "arrow" || e.type === "line" || e.type === "freedraw") && e.points?.length) {
    const xs = e.points.map((p) => p[0] + e.x);
    const ys = e.points.map((p) => p[1] + e.y);
    return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
  }
  const x2 = e.x + e.width;
  const y2 = e.y + e.height;
  return { minX: Math.min(e.x, x2), minY: Math.min(e.y, y2), maxX: Math.max(e.x, x2), maxY: Math.max(e.y, y2) };
}

export function union(boxes: Box[]): Box | null {
  if (boxes.length === 0) return null;
  return boxes.reduce((a, b) => ({ minX: Math.min(a.minX, b.minX), minY: Math.min(a.minY, b.minY), maxX: Math.max(a.maxX, b.maxX), maxY: Math.max(a.maxY, b.maxY) }));
}

/** Whether a click at (x, y) hits [e], within [tolerance] canvas units. Rotation is undone first. */
export function hits(e: El, x: number, y: number, tolerance: number): boolean {
  const [px, py] = unrotate(e, x, y);
  if (e.type === "arrow" || e.type === "line" || e.type === "freedraw") {
    const pts = (e.points ?? []).map(([a, b]) => [a + e.x, b + e.y] as const);
    for (let i = 1; i < pts.length; i++) if (segmentDistance(px, py, pts[i - 1]!, pts[i]!) <= tolerance + (e.strokeWidth ?? 2)) return true;
    return pts.length === 1 && Math.hypot(px - pts[0]![0], py - pts[0]![1]) <= tolerance;
  }
  const b = bounds(e);
  return px >= b.minX - tolerance && px <= b.maxX + tolerance && py >= b.minY - tolerance && py <= b.maxY + tolerance;
}

export function unrotate(e: El, x: number, y: number): [number, number] {
  const angle = e.angle ?? 0;
  if (!angle) return [x, y];
  const b = bounds(e);
  const cx = (b.minX + b.maxX) / 2;
  const cy = (b.minY + b.maxY) / 2;
  const cos = Math.cos(-angle);
  const sin = Math.sin(-angle);
  return [cx + (x - cx) * cos - (y - cy) * sin, cy + (x - cx) * sin + (y - cy) * cos];
}

function segmentDistance(px: number, py: number, a: readonly [number, number], b: readonly [number, number]): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = dx * dx + dy * dy;
  const t = len === 0 ? 0 : Math.max(0, Math.min(1, ((px - a[0]) * dx + (py - a[1]) * dy) / len));
  return Math.hypot(px - (a[0] + t * dx), py - (a[1] + t * dy));
}

/** The text of a shape (bound text element), if it has one. */
export function boundText(e: El, all: Map<string, El>): El | undefined {
  const ref = e.boundElements?.find((b) => b.type === "text");
  const t = ref ? all.get(ref.id) : undefined;
  return t && !t.isDeleted ? t : undefined;
}
